import { compact, isEmpty, orderBy, sumBy } from "lodash";
import Tooltip from "rc-tooltip";
import React, { useCallback, useMemo } from "react";
import { Link, RouteComponentProps, useLocation } from "react-router-dom";
import ExternalLink from "../../../../components/ExternalLink";
import InfoIcon from "../../../../components/icons/InfoIcon";
import DisplayLabels from "../../../../components/labels/DisplayLabels";
import { useLineOfSight } from "../../../../context/lineOfSight";
import { useLocations } from "../../../../context/locations";
import { useStats } from "../../../../context/stats";
import { renderPercentileClassification } from "../../../../context/stats/StatsProvider";
import { useTransmitScales } from "../../../../context/transmitScales";
import { ClassificationValue } from "../../../../utils/hotspotUtils";
import useMapConfig from "../../../../utils/useMapConfig";
import useTransmitScaleConfig from "../../../../utils/useTransmitScaleConfig";
import TablePagination from "../../../Workspace/common/TablePagination";
import usePagination, {
  PaginationKey
} from "../../../Workspace/common/usePagination";
import { DEFAULT_SIMULATION_HEIGHT } from "./SuperSimulationSidebar";

interface Props
  extends RouteComponentProps<{
    id: string;
    simulationUuid: string;
    section: string;
  }> {}

const VisibleLocationsTab: React.FC<Props> = (props) => {
  const { currentSuperSimulation } = useLocations();
  const { getPercentile } = useStats();
  const {
    calculatedTransmitScales,
    isTransmitScaleActive,
    getLocationWitnessRewardUnits
  } = useTransmitScales();
  const toggleIsActive = useTransmitScaleConfig(
    (state) => state.toggleIsActive
  );
  const renderDistance = useMapConfig((state) => state.renderDistance);
  const { currentLocation, locations, setSelectPointsOnMapConfig } =
    useLocations();
  const {
    clearPoints,
    setStartPoint,
    setEndPoint,
    setStartHeight,
    setEndHeight
  } = useLineOfSight();
  const { search: queryParamsString } = useLocation();

  const location = useMemo(
    () =>
      currentLocation ||
      (props.match.params.id ? locations[props.match.params.id] : undefined),
    [currentLocation, locations, props.match.params.id]
  );

  const visibleLocations = useMemo(
    () => currentSuperSimulation?.simulation_report?.vl || [],
    [currentSuperSimulation?.simulation_report?.vl]
  );

  // TODO: the 'visible locations' should exclude the ones with active installs

  const locationsToRender = useMemo(
    () =>
      compact(visibleLocations.map((uuid) => locations[uuid])).map(
        (location) => {
          const transmitScale = calculatedTransmitScales[location.uuid] || 0;
          const witnessRewardUnits = getLocationWitnessRewardUnits(location);
          return {
            witnessedLocation: location,
            transmitScale,
            isActive: isTransmitScaleActive(location.uuid),
            witnessRewardUnits,
            witnessScore:
              witnessRewardUnits != null
                ? transmitScale * witnessRewardUnits
                : 0,
            height: location.height || DEFAULT_SIMULATION_HEIGHT
          };
        }
      ),
    [
      calculatedTransmitScales,
      getLocationWitnessRewardUnits,
      isTransmitScaleActive,
      locations,
      visibleLocations
    ]
  );

  const { startIndex, endIndex } = usePagination(
    useCallback(
      (state) =>
        state.getPaginationInfo(
          PaginationKey.locationWitnessesVisibleLocations,
          locationsToRender.length,
          queryParamsString
        ),
      [locationsToRender.length, queryParamsString]
    )
  );

  const [pocScore, pocScorePercentile] = useMemo(() => {
    const additionalWitnessScoreSum = sumBy(locationsToRender, "witnessScore");
    const pocScore = currentSuperSimulation?.simulation_report?.ps || 0;
    const pocScoreWithActiveLocations = pocScore + additionalWitnessScoreSum;
    return [
      pocScoreWithActiveLocations,
      getPercentile("poc_score", pocScoreWithActiveLocations)
    ];
  }, [
    currentSuperSimulation?.simulation_report?.ps,
    getPercentile,
    locationsToRender
  ]);

  if (isEmpty(locationsToRender)) {
    return (
      <p className="mt-2 text-sm text-gray-500 dark:text-gray-50">
        There are no visible locations from this location based on the super
        simulation. Please note that only locations available at the time of the
        super simulation were included in the simulation.
      </p>
    );
  }

  return (
    <>
      <div className="flex flex-col">
        <p className="text-sm text-gray-500 dark:text-gray-50">
          Adding the activated visible locations below would result in a PoC
          Score of{" "}
          {pocScore.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}{" "}
          which is in the{" "}
          <b>{renderPercentileClassification(pocScorePercentile)}</b> globally.
          <br />
          Note that only locations that existed in the workspace at the time of
          running the simulation were considered.
        </p>

        <div className="mt-2 -mb-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="shadow overflow-hidden sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-500">
                <thead className="bg-gray-50 text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      {locationsToRender.length.toLocaleString(undefined)}{" "}
                      location
                      {locationsToRender.length > 1 ? "s" : ""}
                    </th>

                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                      style={{ minWidth: "11rem" }}
                    >
                      <div className="flex items-center">
                        Witness score
                        <Tooltip
                          placement="top"
                          overlay="The Witness Score is a number that represents the PoC rewards that hotspots can earn for witnessing this hotspot. It is calculated as the product of the transmit scale and witness reward units of this hotspot."
                        >
                          <InfoIcon className="inline-flex ml-1 h-4 w-4" />
                        </Tooltip>
                      </div>
                    </th>

                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Transmit scale
                    </th>

                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Activated
                    </th>

                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                      style={{ minWidth: "11rem" }}
                    >
                      <div className="flex items-center">
                        Witness reward units
                        <Tooltip
                          placement="top"
                          overlay={
                            <>
                              This represents the number of reward units another
                              hotspot gets for witnessing this hotspot. This
                              value ranges from 0.12 to 1 and is calculated by
                              the 'Reward Formula for each Receiver' in{" "}
                              <ExternalLink
                                className="inline-flex text-xs mr-1"
                                text="HIP15"
                                url="https://github.com/helium/HIP/blob/master/0015-beaconing-rewards.md"
                              />
                              . In this table, we calculate this value based on
                              the number of hotspots and locations that can be
                              seen from this location based on the most recent
                              super simulation for the location.
                            </>
                          }
                        >
                          <InfoIcon className="inline-flex ml-1 h-4 w-4" />
                        </Tooltip>
                      </div>
                    </th>

                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Height
                    </th>

                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 text-gray-500 dark:text-gray-50 dark:divide-gray-500">
                  {orderBy(locationsToRender, ["witnessScore"], ["desc"])
                    .splice(startIndex, endIndex)
                    .map(
                      ({
                        witnessedLocation,
                        transmitScale,
                        isActive,
                        witnessRewardUnits,
                        witnessScore,
                        height
                      }) => (
                        <tr key={witnessedLocation.uuid}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Link
                              className="relative flex items-center space-x-3 group"
                              to={`/locations/${witnessedLocation.uuid}/super-simulations`}
                            >
                              <div className="min-w-0 focus:outline-none">
                                <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline">
                                  {witnessedLocation.name}
                                </p>
                                <DisplayLabels
                                  linkedUuid={witnessedLocation.uuid}
                                  size="small"
                                />
                              </div>
                            </Link>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {witnessScore != null ? (
                              <ClassificationValue
                                value={witnessScore}
                                withoutTransmitScaleTooltip
                              />
                            ) : (
                              "-"
                            )}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <ClassificationValue value={transmitScale} />
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <button
                                type="button"
                                className={`relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none ${
                                  isActive
                                    ? "bg-purple-600 dark:bg-gray-100 dark:hover:bg-gray-200"
                                    : "bg-gray-200 dark:bg-gray-500 dark:hover:bg-gray-400"
                                }`}
                                aria-pressed="false"
                                aria-labelledby="toggle-interactive"
                                onClick={() =>
                                  toggleIsActive(witnessedLocation.uuid)
                                }
                              >
                                <span
                                  aria-hidden="true"
                                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white dark:bg-gray-800 shadow transform ring-0 transition ease-in-out duration-200 ${
                                    isActive ? "translate-x-5" : "translate-x-0"
                                  }`}
                                />
                              </button>
                            </div>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {witnessRewardUnits != null ? (
                              <ClassificationValue
                                value={witnessRewardUnits}
                                alternativeContent={witnessRewardUnits.toLocaleString(
                                  undefined,
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  }
                                )}
                                withoutTransmitScaleTooltip
                              />
                            ) : (
                              "-"
                            )}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {renderDistance(height, false)}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {location &&
                              currentSuperSimulation?.height != null && (
                                <Link
                                  to="/locations/line-of-sight"
                                  onClick={() => {
                                    clearPoints();
                                    setSelectPointsOnMapConfig(undefined);
                                    setStartPoint([location.lng, location.lat]);
                                    setEndPoint([
                                      witnessedLocation.lng,
                                      witnessedLocation.lat
                                    ]);
                                    setStartHeight(
                                      Number(currentSuperSimulation.height)
                                    );
                                    setEndHeight(height);
                                  }}
                                  className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400"
                                >
                                  Open LOS
                                </Link>
                              )}
                          </td>
                        </tr>
                      )
                    )}
                </tbody>
              </table>
              <TablePagination
                paginationKey={PaginationKey.locationWitnessesVisibleLocations}
                itemsCount={locationsToRender.length}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VisibleLocationsTab;
